import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';

/**
 * Emitted when an operator triggers partner onboarding for a Standort
 * (IMM-255.2). The bus-driven `PartnerOnboardingInviteDeliverable` reacts to it
 * to send the invitation. Ids only on the payload per ADR 0015 §PII — the
 * location manager's name/e-mail are not carried, and no magic-link token is
 * carried (the link is issued at deliverable-resolve time and only its hash is
 * persisted, the raw value lives only in the invitation mail). Audited under
 * aggregateType `identity.organization-onboarding`.
 */
export const OrganizationOnboardingInvitedPayloadSchema = z.object({
  onboardingId: z.uuid(),
  organizationId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type OrganizationOnboardingInvitedPayload = z.infer<typeof OrganizationOnboardingInvitedPayloadSchema>;

/**
 * Emitted when the partner saves their first wizard step and the onboarding
 * record moves `invited` → `in_progress` (IMM-255.3). The partner has no member
 * account, so the action carries no human actor — the `SYSTEM` actor is stamped
 * (the magic-link session is the partner's authority, not a member identity).
 * Ids + occurredAt only per ADR 0015 §PII — no name/e-mail, no magic-link token.
 * Audited under aggregateType `identity.organization-onboarding`.
 */
export const OrganizationOnboardingInProgressPayloadSchema = z.object({
  onboardingId: z.uuid(),
  organizationId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type OrganizationOnboardingInProgressPayload = z.infer<typeof OrganizationOnboardingInProgressPayloadSchema>;

/**
 * Emitted when the partner submits their onboarding and the record moves
 * `in_progress` → `submitted` (IMM-255.3). Drives the operator-notification
 * deliverable (the workspace Owners / Workspace-Admins are notified out-of-band
 * via the `WorkspaceOperatorContactsLookup`, so no recipient PII rides the
 * payload). `SYSTEM` actor — the partner has no member identity. Ids +
 * occurredAt only per ADR 0015 §PII; the captured consents are persisted on the
 * record, never on the event. Audited under aggregateType
 * `identity.organization-onboarding`.
 */
export const OrganizationOnboardingSubmittedPayloadSchema = z.object({
  onboardingId: z.uuid(),
  organizationId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type OrganizationOnboardingSubmittedPayload = z.infer<typeof OrganizationOnboardingSubmittedPayloadSchema>;

/**
 * Emitted when an operator opens the review surface on a submitted onboarding and
 * the record moves `submitted` → `in_review` (IMM-255.4). The operator IS a
 * workspace member, so the operator's member actor rides the payload (unlike the
 * partner-driven SYSTEM events). Ids + occurredAt only per ADR 0015 §PII. Audited
 * under aggregateType `identity.organization-onboarding`.
 */
export const OrganizationOnboardingInReviewPayloadSchema = z.object({
  onboardingId: z.uuid(),
  organizationId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type OrganizationOnboardingInReviewPayload = z.infer<typeof OrganizationOnboardingInReviewPayloadSchema>;

/**
 * Emitted when an operator approves a submitted/in_review onboarding and the
 * record moves to `approved` (IMM-255.4). On approval the approve handler applies
 * the partner's proposed company data to the location's referenced Company and
 * completes the Standort setup — but the event stays ids-only (ADR 0015 §PII): no
 * company data on the payload. The operator's member actor rides it. Audited
 * under aggregateType `identity.organization-onboarding`.
 */
export const OrganizationOnboardingApprovedPayloadSchema = z.object({
  onboardingId: z.uuid(),
  organizationId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type OrganizationOnboardingApprovedPayload = z.infer<typeof OrganizationOnboardingApprovedPayloadSchema>;

/**
 * Emitted when an operator requests changes on a submitted/in_review onboarding
 * and the record moves to `changes_requested` (IMM-255.4). Drives the
 * change-request mail (which re-issues the partner's magic link + surfaces the
 * notes). The notes live on the record, never on the event — the deliverable
 * reads them back via the resolver (ADR 0015 §PII). The operator's member actor
 * rides it. Audited under aggregateType `identity.organization-onboarding`.
 */
export const OrganizationOnboardingChangesRequestedPayloadSchema = z.object({
  onboardingId: z.uuid(),
  organizationId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type OrganizationOnboardingChangesRequestedPayload = z.infer<
  typeof OrganizationOnboardingChangesRequestedPayloadSchema
>;

/**
 * Emitted when an operator rejects a submitted/in_review onboarding and the
 * record moves to `rejected` (IMM-255.4) — terminal, no data applied. Ids +
 * occurredAt only per ADR 0015 §PII; the operator's member actor rides it.
 * Audited under aggregateType `identity.organization-onboarding`.
 */
export const OrganizationOnboardingRejectedPayloadSchema = z.object({
  onboardingId: z.uuid(),
  organizationId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type OrganizationOnboardingRejectedPayload = z.infer<typeof OrganizationOnboardingRejectedPayloadSchema>;
